import React, { useCallback, useEffect, useState } from 'react'
import { Mail, Send } from 'lucide-react'
import { Alert, Button, Card, Input, Select, StatusBadge, Tag } from '@rallyshift/design-system'
import api from '../api/client'
import { roleLabel } from '../utils/status'

interface Invitation {
  id: string
  email: string
  first_name: string | null
  last_name: string | null
  role: string
  skills: string[] | null
  expires_at: string
}

/**
 * Invite one person, and manage the links that have gone out but not been used yet.
 * An organiser can always withdraw or resend, because "did that email arrive?" is the
 * question they actually have.
 */
const InvitePanel: React.FC<{ onChanged?: () => void }> = ({ onChanged }) => {
  const [pending, setPending] = useState<Invitation[]>([])
  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [role, setRole] = useState('volunteer')
  const [skills, setSkills] = useState<string[]>([])
  const [catalogue, setCatalogue] = useState<string[]>([])

  const [busy, setBusy] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  /** Only ever set when the server could not send the mail itself. */
  const [manualLink, setManualLink] = useState('')

  const load = useCallback(async () => {
    try {
      setPending(await api.invitations.list())
    } catch (e: any) {
      setError(e.message || 'Uitnodigingen konden niet worden geladen.')
    }
  }, [])

  useEffect(() => {
    load()
    api.organizations
      .listSkills()
      .then((rows) => setCatalogue(rows.filter((s) => !s.archived).map((s) => s.name)))
      .catch(() => setCatalogue([]))
  }, [load])

  const invite = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim()) return
    setBusy(true)
    setError('')
    setSuccess('')
    setManualLink('')
    try {
      const created = await api.invitations.create({
        email: email.trim(),
        first_name: firstName.trim() || undefined,
        role,
        skills: skills.length ? skills : undefined,
      })
      if (created.accept_url) setManualLink(created.accept_url)
      setSuccess(`Uitnodiging verstuurd naar ${email.trim()}.`)
      setEmail('')
      setFirstName('')
      setSkills([])
      await load()
      onChanged?.()
    } catch (e: any) {
      setError(e.message || 'Uitnodigen is niet gelukt.')
    } finally {
      setBusy(false)
    }
  }

  const resend = async (invitation: Invitation) => {
    setBusyId(invitation.id)
    setError('')
    setManualLink('')
    try {
      const sent = await api.invitations.resend(invitation.id)
      if (sent.accept_url) setManualLink(sent.accept_url)
      setSuccess(`Nieuwe link verstuurd naar ${invitation.email}.`)
      await load()
    } catch (e: any) {
      setError(e.message || 'Opnieuw versturen is niet gelukt.')
    } finally {
      setBusyId(null)
    }
  }

  const revoke = async (invitation: Invitation) => {
    setBusyId(invitation.id)
    setError('')
    try {
      await api.invitations.revoke(invitation.id)
      await load()
      onChanged?.()
    } catch (e: any) {
      setError(e.message || 'Intrekken is niet gelukt.')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <Card
      title="Vrijwilligers uitnodigen"
      meta="Ze krijgen een link die 14 dagen geldig is."
    >
      {error && (
        <div className="mb-4">
          <Alert variant="danger" onDismiss={() => setError('')}>
            {error}
          </Alert>
        </div>
      )}
      {success && (
        <div className="mb-4">
          <Alert variant="success" onDismiss={() => setSuccess('')}>
            {success}
          </Alert>
        </div>
      )}
      {manualLink && (
        <div className="mb-4">
          <Alert variant="warning" title="E-mail staat nog niet aan">
            Stuur deze link zelf door — wij konden de uitnodiging niet mailen.
            <a
              href={manualLink}
              className="mt-2 block break-all font-semibold underline underline-offset-2"
            >
              {manualLink}
            </a>
          </Alert>
        </div>
      )}

      <form onSubmit={invite} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Input
            label="E-mailadres"
            type="email"
            required
            placeholder="naam@voorbeeld.nl"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Input
            label="Voornaam (optioneel)"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
          <Select
            label="Rol"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            options={[
              { value: 'volunteer', label: roleLabel('volunteer') },
              { value: 'coordinator', label: roleLabel('coordinator') },
              { value: 'admin', label: roleLabel('admin') },
            ]}
          />
        </div>

        {catalogue.length > 0 && (
          <div>
            <p className="mb-2 text-sm font-semibold text-text-primary">
              Vaardigheden (optioneel)
            </p>
            <div className="flex flex-wrap gap-2">
              {catalogue.map((skill) => {
                const chosen = skills.includes(skill)
                return (
                  <Tag
                    key={skill}
                    selected={chosen}
                    onClick={() =>
                      setSkills((prev) =>
                        chosen ? prev.filter((s) => s !== skill) : [...prev, skill],
                      )
                    }
                  >
                    {skill}
                  </Tag>
                )
              })}
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <Button type="submit" loading={busy} icon={<Send className="h-4 w-4" />}>
            Verstuur uitnodiging
          </Button>
        </div>
      </form>

      {pending.length > 0 && (
        <div className="mt-6 border-t border-border pt-4">
          <p className="mb-2 text-sm font-semibold text-text-muted">
            Nog niet geaccepteerd ({pending.length})
          </p>
          <ul className="divide-y divide-border">
            {pending.map((invitation) => (
              <li
                key={invitation.id}
                className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:gap-3"
              >
                <Mail className="h-4 w-4 flex-shrink-0 text-text-muted" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-md text-text-primary">{invitation.email}</p>
                  <p className="text-sm text-text-muted">
                    {roleLabel(invitation.role)} · verloopt{' '}
                    {new Date(invitation.expires_at).toLocaleDateString('nl-NL', {
                      day: 'numeric',
                      month: 'short',
                    })}
                  </p>
                </div>
                <StatusBadge
                  dimension="volunteer"
                  status="invited"
                  size="sm"
                  label="Uitgenodigd"
                />
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    disabled={busyId === invitation.id}
                    onClick={() => resend(invitation)}
                  >
                    Opnieuw sturen
                  </Button>
                  <Button
                    variant="danger"
                    disabled={busyId === invitation.id}
                    onClick={() => revoke(invitation)}
                  >
                    Intrekken
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  )
}

export default InvitePanel
